import { useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import {
  BookOpen,
  Bookmark,
  Eye,
  Star,
  Calendar,
  ChevronLeft,
  Lock,
  Share2,

} from "lucide-react";
import { chapters } from "../data/dummyData";

/* ================= CONSTANTS ================= */
const LAST_READ_KEY = "naray8:lastReadChapter";
const COINS_KEY = "naray8:coins";

const CHAPTERS_PER_ROW = 6;
const DEFAULT_ROWS = 5;
const DEFAULT_VISIBLE = CHAPTERS_PER_ROW * DEFAULT_ROWS;

const UNLOCK_COST = 10;

const commentsData = [
  {
    id: 1,
    user: "Joey Cambel",
    time: "3 months ago",
    text: "Lorem ipsum dolor sit amet consectetur. Vulputate bdit dolor scelerisque proin viverra.",
    likes: 10,
    replies: [
      {
        id: 11,
        user: "Gabriel Jones",
        time: "3 months ago",
        text: "Lorem ipsum dolor sit amet consectetur. Vulputate bdit dolor scelerisque.",
        likes: 1,
        isAuthor: true,
      },
    ],
  },
];

/* ================= COMPONENT ================= */
const StoryDetail = () => {
  const navigate = useNavigate();
  const scrollRef = useRef(null);

  const [comments, setComments] = useState(commentsData);
  const [newComment, setNewComment] = useState("");
  const [coins, setCoins] = useState(
    parseInt(localStorage.getItem(COINS_KEY)) || 50
  );
  const [previewOpen, setPreviewOpen] = useState(false);

  const [showAllChapters, setShowAllChapters] = useState(false);

  const [unlockModalOpen, setUnlockModalOpen] = useState(false);
  const [selectedChapter, setSelectedChapter] = useState(null);

  const unlockedChapters =
    JSON.parse(localStorage.getItem("naray8:unlockedChapters")) || [];

  /* ================= SCROLL RESUME ================= */
  useEffect(() => {
    const lastScroll = localStorage.getItem("naray8:lastScroll");
    if (lastScroll && scrollRef.current) {
      scrollRef.current.scrollTop = parseInt(lastScroll);
    }
  }, []);

  const handleScroll = () => {
    if (scrollRef.current) {
      localStorage.setItem("naray8:lastScroll", scrollRef.current.scrollTop);
    }
  };

  /* ================= LAST READ ================= */
  const lastRead = JSON.parse(localStorage.getItem(LAST_READ_KEY)) ?? null;
  const firstUnlocked = lastRead ?? chapters.find((c) => c.isFree)?.id ?? 0;

  const handleStartReading = () => {
    navigate(`/naray8/story/1/chapter/${firstUnlocked}`);
  };

  /* ================= CHAPTER CLICK ================= */
  const handleChapterClick = (chapter) => {
    const isUnlocked = chapter.isFree || unlockedChapters.includes(chapter.id);

    if (isUnlocked) {
      navigate(`/naray8/story/1/chapter/${chapter.id}`);
      return;
    }

    setSelectedChapter(chapter);
    setUnlockModalOpen(true);
  };

  /* ================= UNLOCK CHAPTER ================= */
  const handleUnlockChapter = () => {
    if (!selectedChapter) return;

    if (coins < UNLOCK_COST) {
      alert("Not enough coins!");
      return;
    }

    const newCoins = coins - UNLOCK_COST;
    setCoins(newCoins);
    localStorage.setItem(COINS_KEY, newCoins);

    const updatedUnlocked = [...unlockedChapters, selectedChapter.id];
    localStorage.setItem(
      "naray8:unlockedChapters",
      JSON.stringify(updatedUnlocked)
    );

    setUnlockModalOpen(false);
    navigate(`/naray8/story/1/chapter/${selectedChapter.id}`);
  };

  const visibleChapters = showAllChapters
    ? chapters
    : chapters.slice(0, DEFAULT_VISIBLE);

  /* ================= POST COMMENT ================= */
  const handlePostComment = () => {
    if (!newComment.trim()) return;

    const tempComment = {
      id: Date.now(),
      user: "You",
      time: "Just now",
      text: newComment,
      likes: 0,
      replies: [],
    };
    setComments([tempComment, ...comments]);
    setNewComment("");

    setTimeout(() => console.log("Comment posted to server:", tempComment), 1000);
  };

  return (
    <div
      ref={scrollRef}
      onScroll={handleScroll}
      className="min-h-screen bg-[#2B2B27] font-sora text-white overflow-y-auto relative"
    >
      {/* ================= HEADER BANNER ================= */}
      <section className="px-4 sm:px-6 pt-6">
        <div className="relative h-[280px] sm:h-[320px] rounded-xl overflow-hidden">
          <button
            onClick={() => navigate(-1)}
            className="absolute top-4 left-4 z-30 flex items-center gap-1 bg-black/40 backdrop-blur px-3 py-1.5 rounded-full text-sm text-white hover:bg-black/60"
          >
            <ChevronLeft size={18} />
            Back
          </button>

          <img
            src="https://i.ytimg.com/vi/qCJN4tON0Ww/maxresdefault.jpg"
            alt="cover"
            className="absolute inset-0 w-full h-full object-cover"
          />

          <div className="absolute inset-0 bg-white/30 z-10" />

          <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 z-20 flex items-center">
            <img
              src="https://i.ytimg.com/vi/qCJN4tON0Ww/maxresdefault.jpg"
              alt="book"
              onClick={() => setPreviewOpen(true)}
              className="w-[150px] sm:w-[200px] h-[240px] sm:h-[323px] cursor-pointer object-cover rounded-lg shadow-2xl transition-transform hover:scale-105"
            />
          </div>
        </div>
      </section>

      {/* ================= ACTION BUTTONS ================= */}
      <section className="px-4 sm:px-6 mt-4 flex flex-col gap-3">
        <button
          onClick={handleStartReading}
          className="h-[50px] w-full sm:w-auto bg-black rounded-lg flex items-center justify-center gap-2"
        >
          <BookOpen size={18} />
          {lastRead !== null ? "Continue Reading" : "Start Reading"}
        </button>

        <button className="h-[50px] w-full sm:w-auto bg-[#3A3A36] rounded-lg flex items-center justify-center gap-2">
          <Bookmark size={18} /> Add to Library
        </button>
      </section>

      {/* ================= STORY INFO ================= */}
      <section className="px-4 sm:px-6 mt-6">
        <h1 className="text-xl sm:text-2xl font-semibold">Lorem gama lore</h1>
        <p className="text-white font-light font-sora mt-1">by Ademolu David</p>

        <div className="flex flex-wrap gap-2 mt-4 text-sm">
          <Badge icon={<Star size={14} />} text="9.0 Rating" />
          <Badge icon={<BookOpen size={14} />} text="551 chapters" />
          <Badge icon={<Eye size={14} />} text="12.4M Views" />
          <Badge icon={<Calendar size={14} />} text="June 2025" />
        </div>
      </section>

      <section className="px-4 sm:px-6 mt-4">
        <h1 className="text-[12px] ">Genres</h1>
        <div className="flex flex-wrap gap-2 mt-2 text-sm">
          <Genres text="Fantasy" />
          <Genres text="Action" />
          <Genres text="Mythology" />
          <Genres text="Real-Life" />
          <Genres text="Adventure" />
        </div>
      </section>

      {/* ================= DESCRIPTION ================= */}
      <section className="px-4 sm:px-6 mt-6 text-sm text-white font-sora font-light leading-relaxed">
        <h1 className="text-[14px] font-semibold">Description</h1>
        <p className="text-white text-[11px] font-light font-sora mt-1">
          Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod
          tempor incididunt ut labore et dolore magna aliqua. Lorem ipsum dolor
          sit amet consectetur adipiscing elit sed do eiusmod tempor incididunt.Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod
          tempor incididunt ut labore et dolore magna aliqua. Lorem ipsum dolor
          sit amet consectetur adipiscing elit sed do eiusmod tempor incididunt.
        </p>
      </section>

      {/* ================= META ================= */}
      <section className="px-4 sm:px-6 mt-4 grid grid-cols-1 sm:grid-cols-2 gap-y-2 text-sm text-white font-light font-sora">
        <span>Status: <b className="text-white">Ongoing</b></span>
        <span>Language: <b className="text-white">English</b></span>
        <span>First publish: <b className="text-white">Oct 11, 2024</b></span>
        <span>Last updated: <b className="text-white">1 day ago</b></span>
      </section>

      {/* ================= TABLE OF CONTENT ================= */}
      <section className="px-2 sm:px-14 mt-8">
        <h2 className="text-[14px] font-semibold mb-2">Table of content</h2>

        <div className="grid grid-cols-2 sm:grid-cols-6 gap-3">
          {visibleChapters.map((ch) => {
            const isUnlocked = ch.isFree || unlockedChapters.includes(ch.id);
            return (
              <button
                key={ch.id}
                onClick={() => handleChapterClick(ch)}
                className={`h-11 px-3 rounded-lg text-xs flex items-center justify-between
                  ${isUnlocked
                    ? "bg-[#3A3A36] hover:bg-[#4A4A45]"
                    : "bg-[#353530] hover:bg-[#44443F]"
                  }`}
              >
                <span className="truncate">{ch.title}</span>
                {!isUnlocked && <Lock size={12} className="text-[#BE9524]" />}
              </button>
            );
          })}
        </div>

        {chapters.length > DEFAULT_VISIBLE && (
          <button
            onClick={() => setShowAllChapters((prev) => !prev)}
            className="mt-4 text-sm text-[#BE9524]"
          >
            {showAllChapters ? "See Less ↑" : "See More ↓"}
          </button>
        )}
      </section>

      {/* ================= AUTHOR CARD ================= */}
      <section className="px-4 sm:px-20 mt-12 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 sm:gap-0">
        <div className="flex items-center gap-4">
          <img
            src="https://i.ytimg.com/vi/qCJN4tON0Ww/maxresdefault.jpg"
            alt="cover"
            className="rounded-full w-16 h-16 sm:w-20 sm:h-20"
          />
          <div className="flex flex-col">
            <p className="font-medium">Gabriel Jones</p>
            <p className="text-sm text-white font-light font-sora">@gabrieljones</p>
          </div>
        </div>
        <button className="h-9 px-4 rounded-lg bg-[#3A3A36] text-sm self-start sm:self-auto">Add to Library</button>
      </section>

      {/* ================= SOCIAL + RATE BUTTONS ================= */}
      <section className="px-4 sm:px-20 mt-4 flex flex-col gap-4">
        <div className="flex flex-wrap sm:flex-nowrap gap-3">
          {/* <button className="h-10 w-28 flex items-center justify-center rounded-lg bg-[#1C1B19] text-white hover:bg-[#4A4A45] gap-2">
            <Heart size={18} className="text-[#BE9524]" />
            <span className="text-[12px]">Follow</span>
          </button> */}

          <button className="h-10 w-16 flex items-center justify-center rounded-lg bg-[#1C1B19] text-white hover:bg-[#4A4A45]">
            <Share2 size={18} />
          </button>

          {/* <button className="h-10 w-16 flex items-center justify-center rounded-lg bg-[#1C1B19] text-white hover:bg-[#4A4A45]">
            <MoreHorizontal size={18} />
          </button> */}
        </div>

        <div className="self-start mt-2 sm:mt-0">
          <button className="inline-flex h-10 px-8 rounded-lg bg-[#1C1B19] items-center justify-center gap-2 text-sm hover:bg-[#4A4A45]">
            <Star size={18} className="text-[#BE9524]" />
            <span>Rate</span>
          </button>
        </div>
      </section>

      {/* ================= COMMENTS ================= */}
      <section className="px-4 sm:px-36 mt-4 pb-32">
        <div className="bg-[#3A3A36] rounded-xl p-3">
          <textarea
            rows={3}
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Write a comment..."
            className="w-full bg-transparent resize-none outline-none text-sm text-white placeholder-gray-400"
          />
          <div className="flex justify-end mt-2">
            <button
              onClick={handlePostComment}
              className="h-9 px-6 bg-black rounded-lg text-sm"
            >
              Post
            </button>
          </div>
        </div>

        <div className="mt-8 space-y-8">
          {comments.length === 0 ? (
            <SkeletonComment />
          ) : (
            comments.map((comment) => (
              <div key={comment.id}>
                <div className="flex gap-3">
                  <img
                    src="https://acf.goodnovel.com/book/202511/22c82796f35cbc8f893561bd94ce3f0a0546adf5bde334652f3fb44b2ba889c3.jpg?v=1&p=1&w=1080&a=webp"
                    alt="cover"
                    className="rounded-full w-12 h-12 "
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-sm">{comment.user}</p>
                      <span className="text-xs text-white font-light font-sora">{comment.time}</span>
                    </div>
                    <p className="text-sm text-gray-300 mt-2">{comment.text}</p>
                    <div className="flex gap-4 mt-2 text-xs text-white font-light font-sora">
                      <button>Like ({comment.likes})</button>
                      <button>Reply</button>
                    </div>
                  </div>
                </div>

                {comment.replies?.length > 0 && (
                  <div className="ml-11 mt-4 space-y-4 border-l border-[#3A3A36] pl-4">
                    {comment.replies.map((reply) => (
                      <div key={reply.id} className="flex gap-3">
                        <img
                          src="https://cos-enres.cdreader.com/site-322(new)/0/87013/coverorgin.jpg?v=03a545cda7f62154e8e6e3fea8e07fc4&imageMogr2/format/webp"
                          alt="cover"
                          className="rounded-full w-12 h-12 "
                        />                        
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium">
                              {reply.user}
                              {reply.isAuthor && <span className="ml-2 text-xs text-[#BE9524]">Author</span>}
                            </p>
                            <span className="text-xs text-white font-light font-sora">{reply.time}</span>
                          </div>
                          <p className="text-sm text-gray-300 mt-1">{reply.text}</p>
                          <button className="text-xs text-white font-light font-sora mt-1">Like ({reply.likes})</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </section>

      {/* ================= MOBILE STICKY ================= */}
      <div className="fixed bottom-0 left-0 w-full bg-[#2B2B27] p-4 flex justify-center sm:hidden">
        <button
          onClick={handleStartReading}
          className="h-[50px] w-full max-w-md bg-black rounded-lg flex items-center justify-center gap-2"
        >
          <BookOpen size={18} />
          {lastRead !== null ? "Continue Reading" : "Start Reading"}
        </button>
      </div>

      {/* ================= PREVIEW ================= */}
      {previewOpen && (
        <div
          onClick={() => setPreviewOpen(false)}
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
        >
          <img
            src="https://i.ytimg.com/vi/qCJN4tON0Ww/maxresdefault.jpg"
            alt="Full preview"
            className="max-w-full max-h-full object-contain rounded-lg"
          />
        </div>
      )}

      {/* ================= COIN UNLOCK MODAL ================= */}
      {unlockModalOpen && selectedChapter && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="bg-[#2B2B27] rounded-xl p-6 w-full max-w-sm text-center">
            <h2 className="text-white font-semibold mb-2">Unlock Chapter</h2>
            <p className="text-gray-300 text-sm mb-4">
              Spend {UNLOCK_COST} coins to unlock "{selectedChapter.title}"?
            </p>
            <div className="flex justify-center gap-4 flex-wrap">
              <button
                onClick={handleUnlockChapter}
                className="px-4 py-2 bg-[#BE9524] rounded-lg font-semibold"
              >
                Unlock
              </button>
              <button
                onClick={() => setUnlockModalOpen(false)}
                className="px-4 py-2 bg-gray-600 rounded-lg font-semibold"
              >
                Cancel
              </button>
            </div>
            <p className="text-sm text-gray-400 mt-3">Coins available: {coins}</p>
          </div>
        </div>
      )}
    </div>
  );
};

/* ================= SMALL COMPONENTS ================= */
const Badge = ({ icon, text }) => (
  <div className="flex items-center gap-1 bg-[#3A3A36] px-3 py-1 rounded-full">{icon}{text}</div>
);

const Genres = ({ text }) => (
  <div className="flex items-center text-[10px] text-black font-sora gap-1 bg-[#9B9B9B] px-3 py-1 rounded-full">{text}</div>
);

const SkeletonComment = () => (
  <div className="animate-pulse space-y-4">
    {[...Array(2)].map((_, i) => (
      <div key={i} className="flex gap-3">
        <div className="w-8 h-8 rounded-full bg-gray-600" />
        <div className="flex-1 space-y-2">
          <div className="h-4 bg-gray-600 rounded w-1/3" />
          <div className="h-3 bg-gray-600 rounded w-full" />
          <div className="h-3 bg-gray-600 rounded w-5/6" />
        </div>
      </div>
    ))}
  </div>
);

export default StoryDetail;
